import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { useDarkMode } from '../../hooks/useDarkMode';
import usePhotos from '../../hooks/usePhotos';
import { PICKER_ACCENT } from './bookingsTheme';

// ─── The office, drawn ────────────────────────────────────────────────────────
//
// WHY THIS WAS REWRITTEN. "Pick a desk is getting a lot of negativity as it looks very
// poor." It did. The previous plan was a correct diagram and a poor picture: pale tints on
// a near-white field, hairline strokes, 1pt text, and the word "FREE" or "taken" printed on
// every rectangle. Nothing on it had any weight, so it read as a spreadsheet of boxes
// rather than as a room you could find a seat in.
//
// The change is not a coat of paint. Three things are different in kind:
//
//   1. IT IS A BUILDING, NOT A CHART. The floor is a surface with a drawn grid, walls have
//      real thickness and are filled the way an architect fills them, rooms sit on the
//      floor rather than beside it, and the whole plan carries a shadow so it reads as one
//      object. Zones are tinted, each its own hue derived from its name, so "the Wilmore
//      end" is somewhere you can see rather than a dashed rectangle with grey text in it.
//
//   2. YOU SEE PEOPLE, NOT THE WORD "TAKEN". An occupied desk shows the person: their
//      initials in their team's colour, and their name. That single change is what turns
//      the plan from a stock list into a picture of who is in — which is the question
//      people are actually asking when they open it. "Taken" told you nothing you could
//      act on; "O. Davie" tells you where your team is sitting.
//
//   3. FREE IS THE LOUDEST THING ON IT. Finding a seat is the only reason anybody comes
//      here, so a free desk is the most saturated, highest-contrast object on the plan and
//      everything else recedes. The desk's own name is what is printed on it, because that
//      is what you tell a colleague once you have taken it.
//
// The interaction underneath — pinch, pan, and telling a tap from a drag — is unchanged and
// deliberately so: it was the fiddly part and it was right.
//
// Coordinates live in a 0-100 horizontal plan space; the vertical space is 0-VH where
// VH = 100 * heightRatio. Desk pos_x/pos_y and zone x/y/w/h are all in that space.
//
// Two modes:
//   • Booking      — tap an available desk to book it, or your own to release it.
//   • Edit (HR)    — drag desks; add / drag / resize / rename / delete rooms and zones.

// How far a press may travel and still count as a tap rather than a drag, in screen
// pixels. Generous enough for a thumb on a phone.
const TAP_SLOP = 6;

// ── Furniture, sized like furniture ──────────────────────────────────────────
//
// Roughly twice as wide as deep, which is what a desk looks like from above. Bigger than
// the previous 8.6 x 4.2 because the tile now has to carry a person's initials and their
// name legibly, and because a plan of confident objects reads better than a plan of small
// ones.
const DESK_W = 10.2;
const DESK_H = 5.0;
// The monitor: a thin block against the back edge. Two of them on a wide desk would be
// fussy at this scale, so one centred block stands for the screen.
const MON_W = 4.6;
const MON_H = 0.85;
// A wide, flat seat tucked close to the desk, back rail on the far side.
const CHAIR_W = 4.4;
const CHAIR_H = 1.6;
const CHAIR_GAP = 0.4;

// ── THE BUILDING, IN THE IDIOM THE TEAM ALREADY KNOWS ───────────────────────
//
// Rewritten a second time against the app they use and like (Team Today), because "similar
// to the thing they already love" beats "better in my opinion" every time.
//
// What that idiom actually is, and it is a coherent set of decisions rather than a look:
//
//   • The canvas is WHITE. No floor texture, no grid, no drop-shadowed frame. The plan is
//     the furniture and nothing else.
//   • Desks are WHITE with a thin grey border, whatever their state. They are the stage.
//   • PEOPLE ARE THE ONLY COLOUR ON THE PLAN. An occupied seat is a filled circle with the
//     person's initials; an empty seat is a white tile with its name on it. Free versus
//     taken is then a difference between something and nothing, which the eye reads faster
//     than a difference between two fills — and it means the plan never looks like a
//     heat map.
//   • Walls and fixtures are plain light-grey blocks with no labels.
//   • Rooms are outlined, not filled, with a small grey name.
//
// I had built the opposite: a saturated architectural drawing with a textured floor, poché
// walls, tinted neighbourhoods and green/amber/teal desks. It was more drawing and less
// tool. The tinted zones are gone entirely, which also disposes of the hue problem that
// took two passes to get right — there is no hue left to get wrong.
const BUILD = {
  floor:      '#ffffff',
  room:       '#ffffff',
  roomEdge:   '#e2e5ec',
  fixture:    '#eceef3',   // walls, pillars, cupboards: quiet grey blocks
  fixtureEdge:'#e0e3ea',
  ink:        '#2c2f4a',
  inkLight:   '#9aa0b4',
  frame:      '#e6e8ef',
  // The hollow ring on a free seat that somebody owns.
  ring:       '#c3c8d4',
};

// The one accent. Indigo carries selection, your own seat, and every control — the same
// single-accent discipline the reference has.
const ACCENT = '#4f46b8';
const ACCENT_SOFT = '#eeecfb';

// ── Desk states ──────────────────────────────────────────────────────────────
//
// Note what is NOT here: a fill per state. The tile stays white and the state is carried by
// its border, its label, and whether somebody is sitting on it.
const STATUS = {
  available: { surface: '#ffffff', edge: '#d9dce5', text: '#5b6070', width: 0.32 },
  selected:  { surface: ACCENT_SOFT, edge: ACCENT,   text: '#3b3486', width: 0.7 },
  mine:      { surface: ACCENT_SOFT, edge: ACCENT,   text: '#3b3486', width: 0.6 },
  taken:     { surface: '#ffffff', edge: '#e3e6ed', text: '#8a90a0', width: 0.3 },
  reserved:  { surface: '#fbfaf7', edge: '#e0d7c4', text: '#8d7a55', width: 0.3 },
  blocked:   { surface: '#f4f5f8', edge: '#dfe2e9', text: '#a2a8b6', width: 0.3 },
};

const LEGEND = {
  available: { label: 'Free',         sw: '#ffffff',    bd: '#d9dce5' },
  mine:      { label: 'Yours',        sw: ACCENT_SOFT,  bd: ACCENT },
  taken:     { label: 'Someone in',   sw: '#ffffff',    bd: '#e3e6ed', dot: true },
  reserved:  { label: 'Reserved',     sw: '#fbfaf7',    bd: '#e0d7c4' },
  blocked:   { label: 'Not bookable', sw: '#f4f5f8',    bd: '#dfe2e9' },
};

// ── THE SAME PLAN AFTER DARK ─────────────────────────────────────────────────
//
// The app's dark mode is a stylesheet of `.dark` overrides on Tailwind classes, which
// cannot reach this component: an SVG's colours are presentation attributes computed in
// JavaScript, so the plan stayed a sheet of white paper on a slate page. Rather than move
// the whole plan into CSS variables, the palette is stated a second time and chosen at
// render time.
//
// It is a restatement rather than a filter over the light values, because inverting a
// palette does not preserve what the palette was for. The light plan works by making a free
// desk the brightest object in the room and letting everything else recede towards the
// white floor; recede towards a dark floor and the same relationship needs a free tile
// LIGHTER than its surroundings and a taken one closer to the floor. So free desks here
// have the palest surface and the strongest border, and taken desks nearly disappear into
// the floor, which is the same instruction as in light mode read the other way round.
const ACCENT_DARK = PICKER_ACCENT.dark;
const ACCENT_SOFT_DARK = '#2c2a55';

const BUILD_DARK = {
  floor:      '#131c2c',   // a shade below the slate card the plan sits in, so it recesses
  room:       '#1b2537',
  roomEdge:   '#3b4761',
  fixture:    '#232f45',
  fixtureEdge:'#2d3a52',
  ink:        '#e2e8f0',
  inkLight:   '#8b98ae',
  frame:      '#334155',
  ring:       '#4a5877',
};

const STATUS_DARK = {
  available: { surface: '#1e2a3e', edge: '#61738f', text: '#e2e8f2', width: 0.32 },
  selected:  { surface: ACCENT_SOFT_DARK, edge: ACCENT_DARK, text: '#dcd8ff', width: 0.7 },
  mine:      { surface: ACCENT_SOFT_DARK, edge: ACCENT_DARK, text: '#dcd8ff', width: 0.6 },
  taken:     { surface: '#18212f', edge: '#313d54', text: '#94a1b6', width: 0.3 },
  reserved:  { surface: '#211d15', edge: '#57482c', text: '#bda474', width: 0.3 },
  blocked:   { surface: '#161d29', edge: '#2a3446', text: '#6d7a8f', width: 0.3 },
};

const LEGEND_DARK = {
  available: { label: 'Free',         sw: STATUS_DARK.available.surface, bd: STATUS_DARK.available.edge },
  mine:      { label: 'Yours',        sw: ACCENT_SOFT_DARK,              bd: ACCENT_DARK },
  taken:     { label: 'Someone in',   sw: STATUS_DARK.taken.surface,     bd: STATUS_DARK.taken.edge, dot: true },
  reserved:  { label: 'Reserved',     sw: STATUS_DARK.reserved.surface,  bd: STATUS_DARK.reserved.edge },
  blocked:   { label: 'Not bookable', sw: STATUS_DARK.blocked.surface,   bd: STATUS_DARK.blocked.edge },
};

// The two palettes as one thing to choose between. Grouped so the component can destructure
// the chosen set under the same names the drawing code already uses (see PALETTE below).
const PALETTE_LIGHT = { BUILD, ACCENT, ACCENT_SOFT, STATUS, LEGEND };
const PALETTE_DARK = {
  BUILD: BUILD_DARK, ACCENT: ACCENT_DARK, ACCENT_SOFT: ACCENT_SOFT_DARK,
  STATUS: STATUS_DARK, LEGEND: LEGEND_DARK,
};

// "Olivia Davie" -> "O. Davie". Several people share a first name, so the surname is the
// part that identifies somebody; the forename is reduced to an initial to fit the tile.
const shortName = (full) => {
  const parts = String(full || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  if (parts.length === 1) return parts[0];
  return `${parts[0][0]}. ${parts[parts.length - 1]}`;
};

const initialsOf = (full) => String(full || '').trim().split(/\s+/)
  .filter(Boolean).map(p => p[0]).join('').slice(0, 2).toUpperCase();

// Montserrat Bold is a wide face. Measured against the rendered plan rather than estimated:
// about 12 characters fit beside an avatar at the occupant size. Longer names are clipped
// here and shown in full in the list beneath the plan.
const fitName = (txt, max = 12) =>
  String(txt || '').length > max ? `${String(txt).slice(0, max - 1)}…` : String(txt || '');

// A profile photo in the avatar circle on a seat, the same size as the initials one.
const Face = ({ id, cx, cy, src, opacity, stroke }) => (
  <g style={{ pointerEvents: 'none' }}>
    <clipPath id={`fp-face-${id}`}><circle cx={cx} cy={cy} r="1.7" /></clipPath>
    <image href={src} x={cx - 1.7} y={cy - 1.7} width="3.4" height="3.4" opacity={opacity}
      preserveAspectRatio="xMidYMid slice" clipPath={`url(#fp-face-${id})`} />
    {stroke && <circle cx={cx} cy={cy} r="1.7" fill="none" stroke={stroke} strokeWidth="0.32" />}
  </g>
);

// PP-2 before PP-10, which is how a person reads a desk label.
const byLabel = (a, b) =>
  String(a.label).localeCompare(String(b.label), undefined, { numeric: true });

// ── THE DESK LIST BEHIND THE COUNT ───────────────────────────────────────────
//
// The plan is the best way to choose a seat when you care where you are sitting, and a
// slow one when you already know: you have to find PP-8 on a drawing before you can tap
// it. The count in the header is where the eye already goes to ask "is there anything
// free", so the answer to "which ones" hangs off it as a dropdown — every desk, its
// state, and one click to take it, without reading the map at all.
//
// It is deliberately NOT a second copy of the list view. The list view is the whole
// screen on a phone; this is a menu that keeps the plan on screen behind it.

// What a desk's state says in one line, in the same words the plan and the list use.
const stateText = (st, desk) => {
  const slot = st.slot && st.slot !== 'full' ? ` (${String(st.slot).toUpperCase()})` : '';
  switch (st.status) {
    case 'mine':     return `Yours${slot}`;
    case 'taken':    return `${st.person || 'Someone in'}${slot}`;
    // Whose seat it is, not why it is theirs. "Reserved — Keeps his seat" is a note
    // written for an office admin; the person reading this list wants to know who they
    // would be turfing out, and the plan already names them on the tile.
    case 'reserved': {
      const who = desk.assigned_person_name || st.note || desk.status_note;
      return `Reserved${who ? ` — ${who}` : ''}`;
    }
    case 'blocked':  return `Not bookable${st.note || desk.status_note ? ` — ${st.note || desk.status_note}` : ''}`;
    default:
      // A seat freed because its owner is elsewhere is only free until they need
      // it again, which is worth saying before somebody takes it for a fortnight.
      // Not "away": they may be at another office, or at another desk in this one.
      // Your own reserved seat is free to you and to nobody else, so it says so
      // rather than reading like any other empty desk.
      if (st.yours) return 'Your desk';
      return st.released && desk.assigned_person_name
        ? `Free — ${String(desk.assigned_person_name).split(/\s+/)[0]} is not at this desk`
        : 'Free';
  }
};

const DOT = {
  available: 'bg-emerald-500',
  mine:      'bg-teal-600',
  taken:     'bg-slate-400',
  reserved:  'bg-amber-500',
  blocked:   'bg-gray-300',
};

export default function FloorPlanMap({
  desks = [],
  getState = () => ({ status: 'available' }),
  selectedId = '',
  onSelect,
  editable = false,
  onMove,
  zones = [],
  onZonesChange,
  heightRatio = 0.58,
  legend = true,
  // Desks worth pointing at — a teammate's neighbours, a search hit. Drawn with a ring, not
  // a fill, so it reads on top of every state without becoming another colour.
  highlightIds = null,
  // Where the desk under the pointer is, in the plan's own 0-100 space, so the parent can
  // anchor a card to it. Reported rather than rendered here: an HTML card can hold real
  // buttons, which SVG cannot.
  onAnchor,
  // "Pride Park — Ground floor". The reference names the floor above the plan, which is
  // worth copying: it is the one thing that tells you at a glance that you are looking at
  // the right office.
  title = '',
  // Let the plan take the whole width it is given. The default cap keeps it legible in
  // a desktop page column; a wall display wants the opposite.
  fill = false,
}) {
  const VH = Math.round(100 * heightRatio);
  // Only the signed-in shell loads photos, so the entrance display keeps initials.
  const photos = usePhotos();
  // The chosen palette, deliberately bound to the same names the drawing code below uses,
  // so switching themes did not mean rewriting every fill and stroke. These shadow the
  // module-level light constants for the length of this function.
  const isDark = useDarkMode();
  const { BUILD, ACCENT, ACCENT_SOFT, STATUS, LEGEND } = isDark ? PALETTE_DARK : PALETTE_LIGHT;
  const svgRef = useRef(null);
  const dragRef = useRef(null);
  const [live, setLive] = useState(null);
  // The seat under the pointer. Held here rather than in CSS because the card that hangs off
  // it needs the person's name and title, which CSS cannot reach.
  const [hover, setHover] = useState(null);
  const zoneEditable = editable && typeof onZonesChange === 'function';
  // Nothing here needs a document-scoped id any more: there are no patterns, gradients or
  // filters left. Two plans on one page cannot collide over anything.
  const highlight = highlightIds instanceof Set ? highlightIds : null;

  // ── Zoom and pan ───────────────────────────────────────────────────────────
  // A 39-desk plan at phone width put desk labels at about 7px, which is why the plan was
  // effectively desktop-only. Zoom is driven through the viewBox rather than a CSS
  // transform so every child keeps its plan coordinates and nothing else here has to know
  // about it.
  const MIN_K = 1, MAX_K = 4;
  const [view, setView] = useState({ k: 1, x: 0, y: 0 });
  // Whether the plan has been given the wheel. Scrolling the page with the pointer over the
  // plan used to zoom it, which made the page feel broken: you would try to read what is
  // below the office and the office would swallow the gesture. The wheel is only the plan's
  // once you have clicked on it, and it goes back to the page the moment you click away.
  const [engaged, setEngaged] = useState(false);
  const pointers = useRef(new Map());
  const pinchRef = useRef(null);
  // The desk a press started on, in viewing mode. See deskDown / onPointerUp.
  const pressRef = useRef(null);

  const vw = 100 / view.k;
  const vh = VH / view.k;

  // Where a desk sits inside the rendered plan, as a percentage of it. The SVG scales to the
  // container, so a percentage is all the parent needs to anchor a card — and it survives
  // zoom and pan for free, because it is derived from the current viewBox.
  const anchorOf = useCallback((d) => {
    if (!d || d.pos_x == null) return null;
    return {
      left: ((d.pos_x - view.x) / vw) * 100,
      top: ((d.pos_y - view.y) / vh) * 100,
      visible: d.pos_x >= view.x && d.pos_x <= view.x + vw && d.pos_y >= view.y && d.pos_y <= view.y + vh,
    };
  }, [view, vw, vh]);
  const selectedDesk = desks.find(d => d.id === selectedId);
  const hoverInfo = (() => {
    if (!hover) return null;
    const d = desks.find(x => x.id === hover.id);
    if (!d) return null;
    const st = getState(d) || {};
    const anchor = anchorOf(d);
    if (!anchor?.visible) return null;
    const person = st.status === 'mine' ? 'You' : st.person;
    if (!person) {
      // An empty seat still earns a card: whose desk it normally is, and whether it is
      // yours to take, are both things you would otherwise have to click to find out.
      //
      // "Empty" here means nobody has BOOKED it, which is not the same as free: a
      // reserved seat and an out-of-service one are both unbooked and neither can be
      // taken. This line used to read "Free today" unconditionally, so every locked
      // desk with a named owner invited a booking that the API would then refuse.
      if (!d.assigned_person_name) return null;
      const note = d.status_note || st.note;
      const owner = d.assigned_person_name;
      const desk =
        st.status === 'reserved' ? `Reserved${note ? ` — ${note}` : ''}`
        : st.status === 'blocked' ? `Not bookable${note ? ` — ${note}` : ''}`
        // Free, and worth saying why: an owned seat that has opened up is only
        // free until its owner needs it again, which changes whether you take it.
        : st.yours ? 'Your desk, reserved for you'
        : st.released ? `Free — ${owner.split(/\s+/)[0]} is not at this desk`
        : 'Free today';
      return { anchor, name: d.label, title: `Normally ${owner}`, desk };
    }
    return {
      anchor, name: person,
      title: st.person_title || null,
      desk: [d.label, st.slot && st.slot !== 'full' ? String(st.slot).toUpperCase() : null].filter(Boolean).join(' · '),
    };
  })();
  useEffect(() => { onAnchor?.(selectedDesk ? anchorOf(selectedDesk) : null); }, [selectedId, anchorOf, selectedDesk, onAnchor]);

  // Keep the visible window inside the plan, so you can never pan into blank space and
  // lose the office.
  const clampView = useCallback((k, x, y) => {
    const kk = Math.max(MIN_K, Math.min(MAX_K, k));
    const w = 100 / kk, h = VH / kk;
    return { k: kk, x: Math.max(0, Math.min(100 - w, x)), y: Math.max(0, Math.min(VH - h, y)) };
  }, [VH]);

  const toPlan = useCallback((e) => {
    const r = svgRef.current.getBoundingClientRect();
    return {
      x: view.x + ((e.clientX - r.left) / r.width) * (100 / view.k),
      y: view.y + ((e.clientY - r.top) / r.height) * (VH / view.k),
    };
  }, [VH, view]);

  // Zoom about a fixed plan point, so the desk under the cursor or between the fingers
  // stays put instead of sliding away.
  const zoomAbout = useCallback((nextK, anchor) => {
    setView(v => {
      const k = Math.max(MIN_K, Math.min(MAX_K, nextK));
      const a = anchor || { x: v.x + (100 / v.k) / 2, y: v.y + (VH / v.k) / 2 };
      const fx = (a.x - v.x) / (100 / v.k);
      const fy = (a.y - v.y) / (VH / v.k);
      return clampView(k, a.x - fx * (100 / k), a.y - fy * (VH / k));
    });
  }, [VH, clampView]);

  // The wheel is bound by hand rather than with onWheel, because React registers its wheel
  // listener passively and preventDefault is a no-op there: the page would scroll as well as
  // the plan zooming. Bound only while engaged, so an unengaged plan never touches the
  // gesture at all.
  useEffect(() => {
    const el = svgRef.current;
    if (!el || editable || !engaged) return;   // in edit mode the wheel always scrolls the page
    const handler = (e) => {
      e.preventDefault();
      zoomAbout(view.k * (e.deltaY < 0 ? 1.15 : 1 / 1.15), toPlan(e));
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [editable, engaged, view.k, toPlan, zoomAbout]);

  // Clicking anywhere off the plan hands the wheel back to the page. Escape does the same,
  // for anyone who reaches for the keyboard rather than clicking into whitespace.
  useEffect(() => {
    if (!engaged) return;
    const away = (e) => { if (!svgRef.current?.contains(e.target)) setEngaged(false); };
    const key = (e) => { if (e.key === 'Escape') setEngaged(false); };
    document.addEventListener('pointerdown', away, true);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', away, true);
      document.removeEventListener('keydown', key);
    };
  }, [engaged]);

  const dist = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

  const clampDesk = (x, y) => ({
    x: Math.max(DESK_W / 2, Math.min(100 - DESK_W / 2, x)),
    y: Math.max(DESK_H / 2 + 1.5, Math.min(VH - DESK_H / 2 - 1, y)),
  });

  const deskDown = (e, desk, clickable) => {
    if (!editable) {
      // Record the press so onPointerUp can decide whether this was a tap on a desk. The
      // desk's own onClick cannot be relied on: this press bubbles to the SVG, which takes
      // pointer capture in order to pan, and capture retargets the click to the SVG — so
      // the desk never receives one.
      if (clickable) pressRef.current = { desk, x: e.clientX, y: e.clientY };
      return;
    }
    e.stopPropagation();
    e.target.setPointerCapture?.(e.pointerId);
    dragRef.current = { kind: 'desk', desk, moved: false };
  };

  const zoneDown = (e, zone, mode) => {
    if (!zoneEditable) return;
    e.stopPropagation();
    e.target.setPointerCapture?.(e.pointerId);
    const p = toPlan(e);
    dragRef.current = { kind: mode, zone, moved: false, offX: p.x - zone.x, offY: p.y - zone.y };
  };

  // Background press: starts a pan (viewing) — in edit mode the background is used for
  // drawing zones, so panning stays out of the way there.
  const bgDown = (e) => {
    // Every press inside the plan arrives here, desks included (their own handler only stops
    // propagation in edit mode), so this is the one place that has to claim the wheel.
    if (!editable) setEngaged(true);
    pointers.current.set(e.pointerId, e);
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinchRef.current = { d: dist(a, b), k: view.k };
      dragRef.current = null;
      return;
    }
    if (editable) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = { kind: 'pan', startX: e.clientX, startY: e.clientY, from: view, moved: false };
  };

  const onPointerMove = (e) => {
    if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, e);

    // Two fingers down: pinch to zoom about the midpoint between them.
    if (pointers.current.size === 2 && pinchRef.current) {
      const [a, b] = [...pointers.current.values()];
      const ratio = dist(a, b) / (pinchRef.current.d || 1);
      const r = svgRef.current.getBoundingClientRect();
      const mid = { clientX: (a.clientX + b.clientX) / 2, clientY: (a.clientY + b.clientY) / 2 };
      zoomAbout(pinchRef.current.k * ratio, {
        x: view.x + ((mid.clientX - r.left) / r.width) * vw,
        y: view.y + ((mid.clientY - r.top) / r.height) * vh,
      });
      return;
    }

    const d = dragRef.current;
    if (!d) return;
    if (d.kind === 'pan') {
      const r = svgRef.current.getBoundingClientRect();
      d.moved = true;
      setView(clampView(
        d.from.k,
        d.from.x - ((e.clientX - d.startX) / r.width) * (100 / d.from.k),
        d.from.y - ((e.clientY - d.startY) / r.height) * (VH / d.from.k),
      ));
      return;
    }
    d.moved = true;
    const p = toPlan(e);
    if (d.kind === 'desk') {
      setLive({ kind: 'desk', id: d.desk.id, ...clampDesk(p.x, p.y) });
    } else if (d.kind === 'move') {
      const x = Math.max(0, Math.min(100 - d.zone.w, p.x - d.offX));
      const y = Math.max(0, Math.min(VH - d.zone.h, p.y - d.offY));
      setLive({ kind: 'zone', id: d.zone.id, x, y, w: d.zone.w, h: d.zone.h });
    } else if (d.kind === 'resize') {
      const w = Math.max(5, Math.min(100 - d.zone.x, p.x - d.zone.x));
      const h = Math.max(4, Math.min(VH - d.zone.y, p.y - d.zone.y));
      setLive({ kind: 'zone', id: d.zone.id, x: d.zone.x, y: d.zone.y, w, h });
    }
  };

  const onPointerUp = (e) => {
    if (e?.pointerId != null) pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchRef.current = null;
    const d = dragRef.current;
    dragRef.current = null;
    const l = live;
    setLive(null);

    // Tap on a desk, decided here rather than by the desk's onClick (see deskDown). Only a
    // real pointerup counts — pointercancel and pointerleave are routed here too and must
    // never book a seat. A press that panned, or that travelled more than a few pixels, is
    // a drag and not a tap.
    const pressed = pressRef.current;
    pressRef.current = null;
    if (e?.type === 'pointerup' && pressed && !(d?.kind === 'pan' && d.moved)
        && Math.hypot(e.clientX - pressed.x, e.clientY - pressed.y) <= TAP_SLOP) {
      onSelect?.(pressed.desk);
      return;
    }

    if (d?.kind === 'pan') return;
    if (!d || !d.moved || !l) return;
    if (d.kind === 'desk') {
      onMove?.(d.desk, Math.round(l.x * 10) / 10, Math.round(l.y * 10) / 10);
    } else {
      const round = (n) => Math.round(n * 10) / 10;
      onZonesChange?.(zones.map(z => z.id === l.id ? { ...z, x: round(l.x), y: round(l.y), w: round(l.w), h: round(l.h) } : z));
    }
  };

  const renameZone = (zone) => {
    const label = window.prompt('Label for this area:', zone.label || '');
    if (label != null) onZonesChange?.(zones.map(z => z.id === zone.id ? { ...z, label } : z));
  };
  const deleteZone = (zone) => onZonesChange?.(zones.filter(z => z.id !== zone.id));

  const liveZone = (z) => (live?.kind === 'zone' && live.id === z.id) ? { ...z, ...live } : z;
  const liveDesk = (d) => (live?.kind === 'desk' && live.id === d.id) ? { ...d, pos_x: live.x, pos_y: live.y } : d;

  const unplaced = desks.filter(d => d.pos_x == null || d.pos_y == null);
  const states = desks.map(d => {
    const st = getState(d) || {};
    return d.id === selectedId ? 'available' : (st.status || 'available');
  });
  const present = new Set(states);
  // Reserved and blocked desks are excluded from the denominator: "9 of 13 free" has to
  // mean 13 desks you could actually have.
  const counts = {
    free:     states.filter(s => s === 'available' || s === 'selected').length,
    taken:    states.filter(s => s === 'taken').length,
    mine:     states.filter(s => s === 'mine').length,
    bookable: states.filter(s => ['available', 'selected', 'taken', 'mine'].includes(s)).length,
  };

  // ── THE DROPDOWN OFF THE COUNT ─────────────────────────────────────────────
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const wrapRef = useRef(null);

  // A desk chosen from the list is reported with WHERE THE LIST IS, so the card that
  // follows a selection can open against the dropdown rather than out on the plan. Picking
  // "PP-8" by name and then having to hunt the plan for the card confirming it would undo
  // the point of the list. Measured rather than assumed, because the header wraps at narrow
  // widths and the button is not always in the same place.
  const pickFromList = (desk) => {
    const wrap = wrapRef.current?.getBoundingClientRect();
    const btn = menuRef.current?.getBoundingClientRect();
    setMenuOpen(false);
    onSelect?.(desk, wrap && btn
      ? { fromList: true, leftPx: btn.left - wrap.left, topPx: btn.bottom - wrap.top }
      : { fromList: true, leftPx: 0, topPx: 0 });
  };

  useEffect(() => {
    if (!menuOpen) return;
    const away = (e) => { if (!menuRef.current?.contains(e.target)) setMenuOpen(false); };
    const key = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('pointerdown', away, true);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', away, true);
      document.removeEventListener('keydown', key);
    };
  }, [menuOpen]);

  // Grouped by area, by the same rule the plan draws them: a desk belongs to the smallest
  // labelled zone containing it. Repeated here rather than passed in because the plan
  // already holds the zone geometry, and the alternative was a second prop that every
  // caller would have to compute the same way.
  const deskMenuGroups = useMemo(() => {
    const areas = zones.filter(z => z.label && !['wall', 'door'].includes(z.type));
    const areaOf = (d) => {
      if (d.pos_x == null || d.pos_y == null) return null;
      const hits = areas.filter(z =>
        d.pos_x >= z.x && d.pos_x <= z.x + z.w && d.pos_y >= z.y && d.pos_y <= z.y + z.h);
      return hits.length ? hits.sort((a, b) => (a.w * a.h) - (b.w * b.h))[0].label : null;
    };
    const groups = new Map();
    for (const d of [...desks].sort(byLabel)) {
      const key = areaOf(d) || 'Elsewhere';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(d);
    }
    return [...groups.entries()]
      .map(([label, list]) => ({ label, desks: list }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [desks, zones]);

  return (
    // Width-capped. Left to fill a wide desktop column, the plan rendered close to 1000px
    // tall and every desk with it. The office is legible well before that, and the zoom
    // control covers anyone who wants closer.
    <div ref={wrapRef} className={`relative ${editable || fill ? '' : 'max-w-[980px]'}`}>
      {/* ── THE CONTROLS ──
          A title row, then the plan's own tools on the right as circular icon buttons, which
          is where the app the team already uses puts them. Muscle memory is worth more than
          my preference about placement. */}
      {!editable && (
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2.5">
          <div className="flex items-baseline gap-2">
            {title && <span className="text-[14px] font-bold text-gray-900">{title}</span>}
            <span className="text-[11px] font-semibold text-gray-500">
              <span style={{ color: counts.free > 0 ? ACCENT : (isDark ? '#f87171' : '#dc2626') }}>{counts.free}</span>
              {' '}of {counts.bookable} desks available
            </span>

            {/* Every desk and its state, for choosing one by name rather than by
                finding it on the drawing. */}
            {onSelect && desks.length > 0 && (
              <div className="relative" ref={menuRef}>
                <button type="button" onClick={() => setMenuOpen(o => !o)}
                  aria-expanded={menuOpen} aria-haspopup="listbox"
                  title="Choose a desk from the list"
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white
                             px-2 py-1 text-[11px] font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400">
                  Choose a desk
                  <span className="text-[8px] leading-none text-gray-500">▼</span>
                </button>

                {menuOpen && (
                  <div className="absolute left-0 top-full mt-1 z-40 w-[17.5rem] max-h-[19rem] overflow-y-auto
                                  rounded-xl border border-gray-200 bg-white shadow-xl"
                    role="listbox">
                    {deskMenuGroups.map(g => (
                      <div key={g.label}>
                        {/* An office with no labelled areas is one group called "Elsewhere",
                            which is a heading that tells you nothing. */}
                        {deskMenuGroups.length > 1 && (
                          <div className="sticky top-0 px-2.5 py-1 bg-gray-50 border-b border-gray-200
                                          text-[9px] font-bold uppercase tracking-wide text-gray-500">
                            {g.label}
                          </div>
                        )}
                        <ul>
                          {g.desks.map(d => {
                            const st = getState(d) || {};
                            const status = d.id === selectedId ? 'available' : (st.status || 'available');
                            // Same rule as a tap on the plan. A desk that has never been
                            // placed on the plan is bookable from here too, since the card
                            // opens against the list rather than against the seat.
                            const pickable = ['available', 'mine'].includes(status);
                            const on = d.id === selectedId;
                            return (
                              <li key={d.id}>
                                <button type="button" disabled={!pickable} role="option" aria-selected={on}
                                  onClick={() => pickFromList(d)}
                                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-left
                                    ${pickable ? 'hover:bg-gray-50 cursor-pointer' : 'opacity-55 cursor-default'}
                                    ${on ? 'bg-gray-50' : ''}`}>
                                  <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${DOT[status] || DOT.available}`} />
                                  <span className="text-[11px] font-bold text-gray-800 w-14 flex-shrink-0">{d.label}</span>
                                  <span className="text-[10px] text-gray-500 flex-1 truncate">
                                    {stateText(st, d)}
                                  </span>
                                  {status === 'mine' && (
                                    <span className="text-[9px] font-bold flex-shrink-0" style={{ color: ACCENT }}>Release</span>
                                  )}
                                  {pickable && status === 'available' && (
                                    <span className="text-[9px] font-bold flex-shrink-0" style={{ color: ACCENT }}>Book</span>
                                  )}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {counts.mine > 0 && (
              <span className="text-[10px] font-semibold rounded-full px-2.5 py-1 mr-1"
                style={{ color: ACCENT, background: ACCENT_SOFT }}>
                ✓ You have a seat
              </span>
            )}
            {[['−', 'Zoom out', () => zoomAbout(view.k / 1.4)],
              ['+', 'Zoom in', () => zoomAbout(view.k * 1.4)],
              ['⤢', 'Fit the whole floor', () => setView({ k: 1, x: 0, y: 0 })]].map(([g, t, fn]) => (
              <button key={t} type="button" onClick={fn} title={t}
                className="w-7 h-7 rounded-full border border-gray-300 bg-white text-gray-600 text-[13px] leading-none
                           hover:border-gray-400 hover:text-gray-900 flex items-center justify-center">
                {g}
              </button>
            ))}
          </div>
        </div>
      )}

      <svg
        ref={svgRef}
        viewBox={`${view.x} ${view.y} ${vw} ${vh}`}
        className="w-full rounded-xl select-none border border-gray-200"
        style={{
          touchAction: 'none', display: 'block', background: BUILD.floor,
          cursor: !editable && view.k > 1 ? 'grab' : undefined,
        }}
        onPointerDown={bgDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={onPointerUp}
      >
        <style>{`
          .fp-clickable { cursor: pointer; }
          .fp-clickable .fp-surface { transition: fill .1s, stroke .1s; }
          .fp-clickable:hover .fp-ring { opacity: 1; }
          .fp-clickable:hover .fp-surface { fill: ${ACCENT_SOFT}; }
        `}</style>

        <rect x="0" y="0" width="100" height={VH} fill={BUILD.floor} />

        {/* ── ZONES, ROOMS AND FIXTURES ──
            Quiet. A room is an outline with a small grey name; a wall or a pillar is a plain
            grey block. Nothing here has a colour, because on this plan colour means a
            person. */}
        {zones.map(z0 => {
          const z = liveZone(z0);
          const cx = z.x + z.w / 2, cy = z.y + z.h / 2;
          const isRoom = z.type === 'room';
          const isWall = z.type === 'wall';
          const isDoor = z.type === 'door';
          const isLabel = z.type === 'label';
          const drag = {
            onPointerDown: e => zoneDown(e, z0, 'move'),
            onDoubleClick: zoneEditable ? (e => { e.stopPropagation(); renameZone(z0); }) : undefined,
            style: { cursor: zoneEditable ? 'move' : 'default' },
          };
          return (
            <g key={z.id}>
              {isWall ? (
                <rect x={z.x} y={z.y} width={z.w} height={z.h} rx="0.3"
                  fill={BUILD.fixture} stroke={BUILD.fixtureEdge} strokeWidth="0.15" {...drag} />
              ) : isDoor ? (
                <g {...drag}>
                  <rect x={z.x - 0.3} y={z.y + z.h - 0.35} width={z.w + 0.6} height="0.7" fill={BUILD.floor} />
                  <line x1={z.x} y1={z.y + z.h} x2={z.x} y2={z.y} stroke={BUILD.roomEdge} strokeWidth="0.35" strokeLinecap="round" />
                  <path d={`M${z.x} ${z.y + z.h} A ${z.w} ${z.h} 0 0 1 ${z.x + z.w} ${z.y + z.h}`}
                    fill="none" stroke={BUILD.roomEdge} strokeWidth="0.22" />
                  {zoneEditable && <rect x={z.x} y={z.y} width={z.w} height={z.h} fill="transparent" />}
                </g>
              ) : isLabel ? null : (
                // A room and an area are drawn the same way: an outline. The difference
                // between "Elmdene" and "North wing" is a label, not a treatment.
                <rect x={z.x} y={z.y} width={z.w} height={z.h} rx={isRoom ? 0.5 : 1.4}
                  fill={isRoom ? BUILD.room : 'transparent'}
                  stroke={BUILD.roomEdge} strokeWidth={isRoom ? 0.45 : 0.3}
                  strokeDasharray={isRoom ? undefined : '1.8 1.4'} {...drag} />
              )}
              {zoneEditable && !isLabel && (
                <>
                  <rect x={z.x + z.w - 1.7} y={z.y + z.h - 1.7} width="1.7" height="1.7" fill={ACCENT} rx="0.3"
                    onPointerDown={e => zoneDown(e, z0, 'resize')} style={{ cursor: 'nwse-resize' }} />
                  <g onClick={() => deleteZone(z0)} style={{ cursor: 'pointer' }}>
                    <circle cx={z.x + z.w - 0.4} cy={z.y + 0.4} r="1.3" fill="#ef4444" />
                    <text x={z.x + z.w - 0.4} y={z.y + 0.4} textAnchor="middle" dominantBaseline="central" fontSize="1.7" fill="#fff" style={{ pointerEvents: 'none' }}>×</text>
                  </g>
                </>
              )}
              {isLabel && z.label && (
                <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
                  fontSize="1.9" fontWeight="600" fill={BUILD.inkLight}
                  style={{ pointerEvents: 'none', letterSpacing: '0.1em' }}>
                  {String(z.label).toUpperCase()}
                </text>
              )}
              {zoneEditable && isLabel && (
                <>
                  <rect x={z.x} y={z.y} width={z.w} height={z.h} fill="transparent"
                    onPointerDown={e => zoneDown(e, z0, 'move')}
                    onDoubleClick={e => { e.stopPropagation(); renameZone(z0); }}
                    style={{ cursor: 'move' }} />
                  <g onClick={() => deleteZone(z0)} style={{ cursor: 'pointer' }}>
                    <circle cx={cx + z.w / 2 - 0.4} cy={cy - z.h / 2 + 0.4} r="1.3" fill="#ef4444" />
                    <text x={cx + z.w / 2 - 0.4} y={cy - z.h / 2 + 0.4} textAnchor="middle" dominantBaseline="central" fontSize="1.7" fill="#fff" style={{ pointerEvents: 'none' }}>×</text>
                  </g>
                </>
              )}
            </g>
          );
        })}

        {/* The extent of the floor. A hairline, because the plan is the furniture — a heavy
            frame around it just draws the eye to the edge of the room. */}
        <rect x="0.15" y="0.15" width="99.7" height={VH - 0.3} fill="none" stroke={BUILD.frame} strokeWidth="0.3" />

        {/* ── DESKS ──
            A white tile with a thin border. If somebody is in, their initials sit on it in a
            filled circle; if not, the tile carries its own name. That is the whole language:
            a seat with a person on it, or a seat with a name on it. */}
        {desks.filter(d => d.pos_x != null && d.pos_y != null).map(d0 => {
          const d = liveDesk(d0);
          const st = getState(d0) || {};
          const isSel = d0.id === selectedId;
          const eff = isSel ? 'selected' : (st.status || 'available');
          const s = STATUS[eff] || STATUS.available;
          const clickable = !editable && eff !== 'blocked' && eff !== 'taken' && eff !== 'reserved' && !!onSelect;
          const cx = d.pos_x, cy = d.pos_y;
          const W = DESK_W, H = DESK_H, x = cx - W / 2, y = cy - H / 2;
          const reason = d0.status_note || st.note;
          const mine = eff === 'mine';
          const occupant = mine ? (st.person || 'You') : (st.person || null);
          const slotBadge = st.slot && st.slot !== 'full' ? String(st.slot).toUpperCase() : null;
          const seated = eff === 'taken' || mine;
          const isFree = eff === 'available' || (isSel && st.status !== 'mine');

          return (
            <g key={d0.id}
              className={`fp-desk${clickable ? ' fp-clickable' : ''}`}
              onPointerDown={e => deskDown(e, d0, clickable)}
              onPointerEnter={() => !editable && setHover({ id: d0.id, x: cx, y: y })}
              onPointerLeave={() => !editable && setHover(h => (h?.id === d0.id ? null : h))}
              style={{ cursor: editable ? 'move' : clickable ? 'pointer' : 'default' }}>
              <title>{[
                d0.label,
                isFree ? 'Free' : null,
                mine ? 'Yours' : null,
                eff === 'taken' && st.person ? st.person : null,
                eff === 'reserved' && `Reserved${reason ? ' — ' + reason : ''}`,
                eff === 'blocked' && 'Not bookable',
              ].filter(Boolean).join(' · ')}</title>

              {/* Worth pointing at: a ring outside the tile, so it never becomes a fill. */}
              {highlight?.has(d0.id) && (
                <rect x={x - 0.8} y={y - 0.8} width={W + 1.6} height={H + 1.6} rx="1.5"
                  fill="none" stroke="#d9a84a" strokeWidth="0.3" style={{ pointerEvents: 'none' }} />
              )}
              {/* Hover ring, revealed by CSS so there is no re-render on mouse move. */}
              <rect className="fp-ring" x={x - 0.55} y={y - 0.55} width={W + 1.1} height={H + 1.1} rx="1.3"
                fill="none" stroke={ACCENT} strokeWidth="0.45" opacity="0" style={{ pointerEvents: 'none' }} />

              {/* The tile. */}
              <rect className="fp-surface" x={x} y={y} width={W} height={H} rx="1.1"
                fill={s.surface} stroke={s.edge} strokeWidth={s.width}
                strokeDasharray={eff === 'blocked' ? '1.1 0.9' : undefined} />

              {seated ? (
                <>
                  {/* ── THE PERSON, AND ONLY THEIR INITIALS ──
                      The reference puts initials on the seat and nothing else, with the name
                      and job title in the card that appears on hover. I had the name on the
                      tile as well, and rendering it showed why they do not: there is room for
                      about eight characters beside an avatar, so "G. Marraffa" came out as
                      "G. Marraf…". A truncated name is worse than no name when a hover and a
                      full list are both a moment away. */}
                  {!mine && photos[st.person_id] ? (
                    <Face id={d0.id} cx={cx} cy={cy - 0.62} src={photos[st.person_id]} />
                  ) : (
                    <>
                      <circle cx={cx} cy={cy - 0.62} r="1.7"
                        fill={mine ? ACCENT : (st.person_color || '#5b6bb5')} />
                      <text x={cx} y={cy - 0.57} textAnchor="middle" dominantBaseline="central"
                        fontSize={mine ? 1.5 : 1.32} fontWeight="700" fill="#ffffff" style={{ pointerEvents: 'none' }}>
                        {mine ? '✓' : initialsOf(occupant)}
                      </text>
                    </>
                  )}
                  <text x={cx} y={cy + 1.62} textAnchor="middle" dominantBaseline="central"
                    fontSize="1.02" fontWeight="600" fill={mine ? s.text : BUILD.inkLight}
                    style={{ pointerEvents: 'none' }}>
                    {mine ? `You · ${d0.label}` : `${d0.label}${slotBadge ? ` · ${slotBadge}` : ''}`}
                  </text>
                </>
              ) : (
                <>
                  {/* ── A RESERVED SEAT SHOWS WHOSE IT IS ──
                      A reserved desk is somebody's seat, so it names them the way an occupied
                      one does. The difference is drawn, not written: the avatar is outlined and
                      grey rather than filled in their team colour, because they are not sitting
                      there and the seat is not yours to take. Word-only tiles all said
                      "Reserved" and left you to hover six of them to find whose was whose.
                      Blocked desks are deliberately excluded — out of service is a fact about
                      the furniture, and putting a person's initials on one would suggest the
                      seat is theirs rather than broken. */}
                  {eff === 'reserved' && d0.assigned_person_name ? (
                    <>
                      {photos[d0.assigned_person_id] ? (
                        // Their photo, faded and still outlined: their seat, but they are not in it.
                        <Face id={d0.id} cx={cx} cy={cy - 0.62} src={photos[d0.assigned_person_id]}
                          opacity={0.5} stroke={s.edge} />
                      ) : (
                        <>
                          <circle cx={cx} cy={cy - 0.62} r="1.7"
                            fill={s.surface} stroke={s.edge} strokeWidth="0.32" />
                          <text x={cx} y={cy - 0.57} textAnchor="middle" dominantBaseline="central"
                            fontSize="1.25" fontWeight="700" fill={s.text} style={{ pointerEvents: 'none' }}>
                            {initialsOf(d0.assigned_person_name)}
                          </text>
                        </>
                      )}
                      <text x={cx} y={cy + 1.62} textAnchor="middle" dominantBaseline="central"
                        fontSize="0.95" fontWeight="500" fill={BUILD.inkLight}
                        style={{ pointerEvents: 'none' }}>{`Reserved · ${d0.label}`}</text>
                    </>
                  ) : (
                    <>
                      {/* An empty seat says what it is called. */}
                      <text x={cx} y={cy + 0.05} textAnchor="middle" dominantBaseline="central"
                        fontSize={eff === 'reserved' || eff === 'blocked' ? 1.15 : 1.5}
                        fontWeight={isFree ? 600 : 500} fill={s.text}
                        style={{ pointerEvents: 'none' }}>
                        {eff === 'reserved' ? 'Reserved' : eff === 'blocked' ? 'Not bookable' : d0.label}
                      </text>
                      {(eff === 'reserved' || eff === 'blocked') && (
                        <text x={cx} y={cy + 1.5} textAnchor="middle" dominantBaseline="central"
                          fontSize="0.95" fontWeight="500" fill={BUILD.inkLight}
                          style={{ pointerEvents: 'none' }}>{d0.label}</text>
                      )}
                      {/* Whose desk this normally is, when it is free. */}
                      {isFree && d0.assigned_person_name && (
                        <circle cx={x + W - 1.15} cy={y + 1.15} r="0.68"
                          fill="none" stroke={BUILD.ring} strokeWidth="0.32" style={{ pointerEvents: 'none' }} />
                      )}
                    </>
                  )}
                </>
              )}
            </g>
          );
        })}

        {/* ── AREA NAMES, LAST ──
            After the desks, because a desk drawn over a label hid it. Small and grey: an
            area is a place to look, not a thing to look at. */}
        {zones.map(z0 => {
          const z = liveZone(z0);
          if (!z.label || ['wall', 'door', 'label'].includes(z.type)) return null;
          return (
            <text key={`lbl-${z.id}`} x={z.x + 1.1} y={z.y + 1.9}
              fontSize="1.55" fontWeight="600" fill={BUILD.inkLight}
              style={{ pointerEvents: 'none', letterSpacing: '0.05em' }}>
              {z.label}
            </text>
          );
        })}
      </svg>

      {/* ── THE HOVER CARD ──
          A dark card naming the person, what they do, and the seat — the reference's one
          flourish, and the only thing on the plan that tells you WHO "OD" is without
          clicking. Positioned in HTML over the SVG because it has to sit above everything
          and hold real type. */}
      {!editable && hoverInfo && (
        <div className="absolute z-20 pointer-events-none"
          style={{
            left: `${hoverInfo.anchor.left}%`, top: `${hoverInfo.anchor.top}%`,
            transform: 'translate(-50%, -115%)',
          }}>
          <div className="rounded-lg px-2.5 py-1.5 shadow-lg whitespace-nowrap"
            style={{ background: isDark ? '#414873' : '#2c2f4a' }}>
            <div className="text-[11px] font-bold text-white leading-tight">{hoverInfo.name}</div>
            {hoverInfo.title && <div className="text-[10px] text-white/70 leading-tight">{hoverInfo.title}</div>}
            <div className="text-[10px] text-white/50 leading-tight">{hoverInfo.desk}</div>
          </div>
        </div>
      )}

      {/* Legend. Quiet, and only the states actually on the plan. */}
      {legend && (
        <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5">
          {Object.entries(LEGEND).filter(([k]) => present.has(k)).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1.5 text-[10px] font-medium text-gray-500">
              {v.dot
                ? <span className="inline-block w-3.5 h-3.5 rounded-full" style={{ background: '#5b6bb5' }} />
                : <span className="inline-block w-5 h-3.5 rounded" style={{ background: v.sw, border: `1px solid ${v.bd}` }} />}
              {v.label}
            </span>
          ))}
          {!editable && (
            <span className="text-[10px] text-gray-400 ml-auto hidden sm:inline">
              {engaged
                ? 'Scroll or pinch to zoom · Esc to scroll the page again'
                : 'Click the plan, then scroll or pinch to zoom · drag to move around'}
            </span>
          )}
        </div>
      )}

      {/* desks not yet placed */}
      {editable && unplaced.length > 0 && (
        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="text-[8px] font-bold text-amber-700 uppercase tracking-wide mb-1">Not yet placed — click to drop onto the plan, then drag into position</div>
          <div className="flex flex-wrap gap-1">
            {unplaced.map(d => (
              <button key={d.id} onClick={() => onMove?.(d, 50, VH / 2)}
                className="px-2 py-1 rounded border border-amber-300 bg-white text-[9px] font-semibold text-amber-700 hover:bg-amber-100">{d.label} +</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
